import { z } from 'zod';

import type {
  AdminRateCard,
  DeskPackageConfiguration,
  PackageDurationUnit,
  RateCardUnit,
} from '@mds/shared/types/rate-card';

import { packageAccessLabel, packageDurationLabel } from '@mds/shared/lib/packages';

import { dollars, optionalText, requiredText, wholeNumber } from '@/lib/validation';

// The wording is shared with the public site; admin reads it under shorter names.
export {
  WEEKDAYS,
  packageAccessLabel as accessLabel,
  packageDurationLabel as durationLabel,
} from '@mds/shared/lib/packages';

/**
 * How a desk package's rules read, and the defaults a new one starts from.
 *
 * The rules are versioned on the rate card and frozen onto every booking made
 * with them, so the same wording serves the editor, the booking details and
 * the payment forms.
 */

/** A desk package is sold by the day, week or month — nothing else. */
export const PACKAGE_UNITS: readonly PackageDurationUnit[] = ['day', 'week', 'month'];

export const isPackageUnit = (unit: RateCardUnit): unit is PackageDurationUnit =>
  (PACKAGE_UNITS as readonly string[]).includes(unit);

/** Package access an administrator may set: 8 AM to midnight, in half-hour steps. */
export const ACCESS_EARLIEST_START = 8 * 60;
export const ACCESS_LATEST_END = 24 * 60;
export const ACCESS_STEP = 30;

/** Each saved package's access times, by package ID. */
export type SavedAccess = ReadonlyMap<string, { startMinute: number; endMinute: number }>;

/**
 * The access times the rate card has saved now. A package may keep these exactly as they are even
 * when they predate the 8 AM – midnight limit; the API holds only new or changed times to it.
 */
export const savedAccessOf = (card: AdminRateCard | undefined): SavedAccess =>
  new Map(
    (card?.deskPackages ?? []).flatMap((p) =>
      p.package.access
        ? [
            [
              p.id,
              { startMinute: p.package.access.startMinute, endMinute: p.package.access.endMinute },
            ],
          ]
        : [],
    ),
  );

/** What a new package, or one the API has never seen rules for, starts as. */
export const defaultPackage = (unit: PackageDurationUnit): DeskPackageConfiguration => ({
  isAvailable: true,
  duration: { count: 1, unit },
  access: null,
  eligibility: { kind: 'none', instructions: '' },
  benefits: [],
});

/** One line for a table cell. */
export const packageSummary = (p: DeskPackageConfiguration) =>
  [
    packageDurationLabel(p.duration),
    p.access ? packageAccessLabel(p.access) : null,
    p.eligibility.kind === 'student_verification' ? 'Students' : null,
    p.benefits.length > 0
      ? `${p.benefits.length} benefit${p.benefits.length === 1 ? '' : 's'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** "Desk access" → "desk-access", matching the API's stable-key rule. */
export const benefitIdFrom = (title: string, taken: ReadonlySet<string>) => {
  const base =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 56) || 'benefit';
  let id = base;
  for (let n = 2; taken.has(id); n += 1) id = `${base}-${n}`;
  return id;
};

/* ---------------------------------------------------------- validation --- */

/**
 * One package's rules as the editor holds them, checked against the API's
 * `deskPackageConfigurationSchema`. The term's unit is not checked here: it is
 * set from the row's price unit, so a mismatch is reported on that unit.
 */
export const packageRulesSchema = z.object({
  isAvailable: z.boolean(),
  duration: z.object({ count: wholeNumber({ min: 1, max: 60 }), unit: z.string() }),
  access: z
    .object({
      daysOfWeek: z.array(z.number()).min(1, 'Pick at least one day it can be used.'),
      // Held to 8 AM – midnight on the row instead (`packageRow`), where the
      // times a package already has saved are known and may stay as they are.
      startMinute: z.number(),
      endMinute: z.number(),
    })
    .refine((w) => w.endMinute > w.startMinute, {
      message: 'Access has to end after it starts.',
      path: ['endMinute'],
    })
    .nullable(),
  eligibility: z
    .object({ kind: z.string(), instructions: optionalText(500) })
    .refine((e) => e.kind !== 'student_verification' || e.instructions.trim() !== '', {
      message: 'Tell the front desk what student proof to check.',
      path: ['instructions'],
    }),
  // The editor stops at 20 benefits, so the API's cap needs no message here.
  benefits: z.array(
    // Only a title is asked for; a description saved earlier rides along unedited.
    z.object({ id: z.string(), title: requiredText(120), description: z.string() }),
  ),
});

/** One row of any of the three lists, with its amount still in typed dollars. */
const rateRow = z.object({
  name: requiredText(120),
  amount: dollars({ max: 1_000_000_000 }),
  unit: z.string(),
  note: optionalText(500),
});

/**
 * A desk package row. Its access times are held to 8 AM – midnight in half-hour
 * steps unless they are exactly the times the package already has saved, as the
 * API does.
 */
const packageRow = (saved: SavedAccess) =>
  rateRow
    .extend({
      id: z.string(),
      unit: z.enum(PACKAGE_UNITS, { error: 'Price a desk package by the day, week or month.' }),
      package: packageRulesSchema.optional(),
    })
    .refine((row) => !row.package || row.package.duration.unit === row.unit, {
      message: 'The term is counted in the unit the package is priced by. Pick that unit again.',
      path: ['unit'],
    })
    .superRefine((row, ctx) => {
      const access = row.package?.access;
      if (!access) return;
      const kept = saved.get(row.id);
      if (kept?.startMinute === access.startMinute && kept.endMinute === access.endMinute) return;
      if (access.startMinute < ACCESS_EARLIEST_START || access.startMinute % ACCESS_STEP !== 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['package', 'access', 'startMinute'],
          message: 'Access can start at 8 AM at the earliest.',
        });
      }
      if (access.endMinute > ACCESS_LATEST_END || access.endMinute % ACCESS_STEP !== 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['package', 'access', 'endMinute'],
          message: 'Access has to end by midnight.',
        });
      }
    });

/**
 * The whole rate card draft and its change summary, as `PUT /admin/rate-card`
 * would check it, given the access times the card has saved now. The list caps
 * (100 rows each) are enforced by Add row.
 */
export const rateCardSchema = (saved: SavedAccess) =>
  z.object({
    changeSummary: optionalText(500),
    spaceRates: z.array(rateRow),
    deskPackages: z.array(packageRow(saved)),
    addOns: z.array(rateRow),
  });
